import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchFaqs } from '../store/actions/faqActions';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import AdSidebar from '../components/ads/AdSidebar';
import { sidebarAds } from '../data/adData';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.5rem 0;
`;

const Text = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 2rem 0;
  line-height: 1.6;
`;

const FaqRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const FaqMain = styled.div`
  flex: 1;
  max-width: 48rem;
  min-width: 0;
`;

const FaqSidebar = styled.aside`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 16rem;
    flex-shrink: 0;
    padding-top: 0;
  }
`;

const AccordionList = styled.div`
  width: 100%;
`;

const AccordionItem = styled.div`
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  margin-bottom: 0.75rem;
  overflow: hidden;
`;

const AccordionTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  text-align: left;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  background: none;
  border: none;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
  }
`;

const AccordionIcon = styled.span`
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const AccordionPanel = styled(motion.div)`
  overflow: hidden;
`;

const AccordionAnswer = styled.p`
  padding: 0 1.25rem 1.25rem 1.25rem;
  margin: 0;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.6;
  border-top: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.06)'};
  padding-top: 0.75rem;
  margin-top: 0;
`;

function FAQ() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const [openId, setOpenId] = useState(null);
  const { items, loading, error } = useSelector((state) => state.faq);

  useEffect(() => {
    dispatch(fetchFaqs());
  }, [dispatch]);

  return (
    <PageLayout>
      <SeoHead title="FAQ | CiteMatch" description="Frequently asked questions about CiteMatch and site discovery." />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>
        <Title>FAQ</Title>
        <Text>Frequently asked questions about CiteMatch and site discovery.</Text>
        {error && <p style={{ color: 'var(--crimson)' }}>{error}</p>}

        <FaqRow>
          <FaqMain>
            <AccordionList>
              {loading && items.length === 0 ? null : items.map((item) => (
                <AccordionItem key={item.id}>
                  <AccordionTrigger
                    type="button"
                    onClick={() => setOpenId(openId === item.id ? null : item.id)}
                    aria-expanded={openId === item.id}
                  >
                    <span>{item.question}</span>
                    <AccordionIcon $open={openId === item.id}>
                      <ChevronDown size={20} />
                    </AccordionIcon>
                  </AccordionTrigger>
                  <AnimatePresence initial={false}>
                    {openId === item.id && (
                      <AccordionPanel
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <AccordionAnswer>{item.answer}</AccordionAnswer>
                      </AccordionPanel>
                    )}
                  </AnimatePresence>
                </AccordionItem>
              ))}
            </AccordionList>
          </FaqMain>
          <FaqSidebar aria-label="Sponsored">
            {sidebarAds.map((ad) => (
              <AdSidebar key={ad.id} ad={ad} />
            ))}
          </FaqSidebar>
        </FaqRow>
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default FAQ;
